import {
  AuthFields,
  AuthHeading,
  AuthNote,
  AuthShell,
  AuthStack,
  authActionClass,
  Button,
  PasswordField,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";
import { emailProblem, type Problem, passwordProblem, signInProblem } from "../auth-copy";
import { landingFor } from "../guards";
import { isAuthError, meQuery, signIn } from "../session";

export const HELP_LINE = "Need help? Contact your MajiTap administrator.";

/** Sign in to the console (`YqEoP`, locked: `yrYEc`). */
export function SignInPage(): ReactNode {
  const search = useSearch({ from: "/sign-in" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState<string>();
  const [problem, setProblem] = useState<Problem>({});
  const [lockedEmail, setLockedEmail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const locked = lockedEmail !== null && lockedEmail === email.trim().toLowerCase();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const badEmail = emailProblem(email);
    const badPassword = passwordProblem(password);
    setEmailError(badEmail);
    setProblem(badPassword ? { password: badPassword } : {});
    if (badEmail || badPassword) return;

    setPending(true);
    try {
      await signIn(email.trim(), password);
      const me = await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
      await navigate({ href: landingFor(me, search.next) });
    } catch (error) {
      const next = signInProblem(isAuthError(error) ? error.code : "UNKNOWN", email.trim());
      if (next.locked) setLockedEmail(email.trim().toLowerCase());
      setProblem(next);
      setPending(false);
    }
  };

  const alert = locked ? signInProblem("ACCOUNT_LOCKED", email).alert : problem.alert;
  return (
    <AuthShell product="Admin console" helpLine={HELP_LINE}>
      <form onSubmit={submit} noValidate>
        <AuthStack>
          <AuthHeading title="Sign in to the console" description="Use the email and password from your invitation." />
          {search.notice === "password-set" && !alert ? (
            <Alert tone="success" title="Your password is set">
              Sign in with your work email and the password you chose.
            </Alert>
          ) : null}
          {alert ? (
            <Alert tone="danger" title={alert.title}>
              {alert.body}
            </Alert>
          ) : null}
          <AuthFields>
            <Input
              label="Work email"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              description="The address your invitation was sent to."
              error={emailError}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <PasswordField
              label="Password"
              name="password"
              autoComplete="current-password"
              description="At least 8 characters."
              error={problem.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </AuthFields>
          <Button type="submit" variant="primary" className={authActionClass} loading={pending} disabled={locked}>
            Sign in
          </Button>
          {/* a new password also ends a lockout, so it stays when locked */}
          <AuthNote>
            <TextButton
              onClick={() => navigate({ to: "/forgot-password", search: { email: email.trim() || undefined } })}
            >
              Forgot your password?
            </TextButton>
          </AuthNote>
        </AuthStack>
      </form>
    </AuthShell>
  );
}
